import { Controller } from 'stimulus'
import { presentAlert } from '~/admin/helpers/notices'

const CSRF_TOKEN = () =>
  document.querySelector('meta[name="csrf-token"]')?.content

// The root row that opens the media dialog (Admin::LinkPickerHelper::MEDIA_FILE_VIRTUAL_PATH).
const MEDIA_FILE_VIRTUAL_PATH = 'media_library_file'

const REFUSED_PICK_MESSAGE = "Couldn't link that item. It may not be live."
const FAILED_PICK_MESSAGE = "Couldn't link that item. Please try again."

// A picker can sit inside another pane stack (the page builder's settings
// sidebar), whose own active pane also matches `.t-pane--active <anything>`.
// Only the element's nearest pane says whether it is on top of this picker.
const inActivePane = (element) =>
  element?.closest?.('.t-pane')?.classList?.contains('t-pane--active')

// Typing pause after which a hand-typed URL is committed as a `change`.
const TYPED_CHANGE_DELAY = 300

// A pane's search field: Menu's local search, or an object picker's server search.
const PANE_SEARCH_SELECTOR =
  '[data-menu-target~="searchBox"], [data-object-picker--mini-target~="searchInput"]'

// A value already carrying a scheme (`https:`, `mailto:`, `tel:`, `simplero:`), a merge tag, a
// fragment or a relative path is a link as typed. Anything else is completed to https:// when
// the field is left, and only if it looks like a bare domain. Mirrors shouldAddHttpsScheme in
// the React picker — without this a picked `{{content_url}}` is saved as `https://{{content_url}}`.
// complete_link_href.js copies these rules for the dialog save and must stay aligned.
const KEPT_AS_TYPED = /^\s*(([a-zA-Z][a-zA-Z0-9+.-]*:)|[{#/])/
const LOOKS_LIKE_DOMAIN = /^\s*\w.*\./

// Schemes that execute instead of navigating. Nothing between this field and the public page
// sanitizes the href a picked value becomes, so the picker must never hand one back: it is
// completed to https:// like any other text, which makes it inert.
const UNSAFE_SCHEMES = ['javascript', 'data', 'vbscript']

// A browser drops whitespace and control characters out of an href before it reads the scheme,
// so `java&#9;script:` runs. Read it the same way rather than trusting how it was typed.
const schemeOf = (value) =>
  value
    // eslint-disable-next-line no-control-regex -- stripping control characters is the point
    .replace(/[\u0000-\u0020]/g, '')
    .toLowerCase()
    .match(/^([a-z][a-z0-9+.-]*):/)?.[1] || null

// Mirrors the input's preview classes in link_picker_component.html.erb —
// text-transparent does not reach ::placeholder.
const PREVIEW_INPUT_CLASSES = [
  'text-transparent',
  'caret-transparent',
  'select-none',
  'placeholder:text-transparent',
]

export default class extends Controller {
  static targets = [
    'hiddenInput',
    'input',
    'preview',
    'previewLabel',
    'previewIcon',
    'previewOpen',
  ]

  static values = {
    pickerUrl: String,
    currentSiteId: Number,
    currentLandingPageId: Number,
    currentFunnelId: Number,
    excludedPaths: String,
    openUrl: String,
    onPageSource: String,
    onPageItems: String,
    groupedResources: String,
  }

  connect() {
    this._fetchController = null
    this._restoreController = null
    this._fetchSelectsOwnLink = false
    this._autoAddedHttps = false
    this._onSetValue = (event) =>
      this.setValue(event.detail.value, event.detail.label)
    // Every key is optional, and every one REPLACES what the previous caller set — the
    // "Insert a link" dialog is shared, so opening it from a different editor must offer that
    // editor's targets rather than accumulating everyone's.
    this._onConfigure = (event) => {
      const { excludedPaths, onPageItems, groupedResources } = event.detail
      let changed = false

      if (excludedPaths !== undefined) {
        // Exclusions are a set — order carries no meaning, so compare them sorted.
        const current = JSON.stringify(this._parsedExcludedPaths().sort())
        changed =
          JSON.stringify([...excludedPaths].sort()) !== current || changed
        this.excludedPathsValue = JSON.stringify(excludedPaths)
      }
      if (onPageItems !== undefined) {
        const next = this._listJson(onPageItems)
        changed = next !== this._listJson(this.onPageItemsValue) || changed
        this.onPageItemsValue = next
      }
      if (groupedResources !== undefined) {
        const next = this._listJson(groupedResources)
        changed = next !== this._listJson(this.groupedResourcesValue) || changed
        this.groupedResourcesValue = next
      }

      // The root pane was server-rendered for the previous caller — refetch it so the items
      // on offer are the ones this caller asked for.
      if (changed) this._fetchPane(null, null, { replaceAll: true })
    }
    this.element.addEventListener('link-picker:set-value', this._onSetValue)
    this.element.addEventListener('link-picker:configure', this._onConfigure)
    this._onPaneKeydown = (event) => this.onPaneKeydown(event)
    this._onPanePopped = () => this._focusPaneSearch(this._findPanesElement())
    this._onDropdownEscape = (event) => {
      if (event.target === this._dropdownElement() && this._popPane())
        event.preventDefault()
    }
    this._onDropdownHidden = (event) => this.onDropdownHidden(event)
    this._onFocusin = (event) => {
      if (this._restoringInputFocus) event.stopPropagation()
    }
    this.element.addEventListener('keydown', this._onPaneKeydown, true)
    this.element.addEventListener('panes:popped', this._onPanePopped)
    this.element.addEventListener('dropdown:escape', this._onDropdownEscape)
    this.element.addEventListener('dropdown:hidden', this._onDropdownHidden)
    this.element.addEventListener('focusin', this._onFocusin, true)
  }

  disconnect() {
    // A typed value still waiting out the delay is worth committing while the field is only
    // losing its controller (a pane move re-connects it); once it has left the page the event
    // has nothing left to bubble to.
    if (this._typedChangeTimer && this.element.isConnected) this._commit()
    this._cancelTypedCommit()
    this._fetchController?.abort()
    this._restoreController?.abort()
    this.element.removeEventListener('link-picker:set-value', this._onSetValue)
    this.element.removeEventListener('link-picker:configure', this._onConfigure)
    this.element.removeEventListener('keydown', this._onPaneKeydown, true)
    this.element.removeEventListener('panes:popped', this._onPanePopped)
    this.element.removeEventListener('dropdown:escape', this._onDropdownEscape)
    this.element.removeEventListener('dropdown:hidden', this._onDropdownHidden)
    this.element.removeEventListener('focusin', this._onFocusin, true)
  }

  /* ===== Input events ===== */

  onInput(event) {
    if (this._previewVisible()) this._hidePreview()
    const value = event.target.value
    this.hiddenInputTarget.value = value
    this._autoAddedHttps = false
    this.hiddenInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
    // Listeners save on change (the page builder saves the node), so a half-typed URL must not
    // be committed per keystroke.
    this._scheduleTypedCommit()
  }

  // Something outside the picker wrote the input (the field header's Personalize dropdown
  // inserts a merge tag and announces only `change`), so the hidden input has to catch up.
  // A native change on blur has already been synced by onInput, and is ignored here.
  onChange(event) {
    if (event.target.value === this.hiddenInputTarget.value) return

    this.onInput(event)
  }

  onBlur() {
    const value = this.inputTarget.value.trim()

    if (this._needsHttps(value)) {
      const withHttps = `https://${value}`
      this.inputTarget.value = withHttps
      this.hiddenInputTarget.value = withHttps
      this._autoAddedHttps = true
      this._commit()
      return
    }

    // Leaving the field ends the typing the delay was waiting out, and whatever replaces the
    // field next (selecting another builder node) would take a pending commit down with it.
    if (this._typedChangeTimer) this._commit()
  }

  _needsHttps(value) {
    if (!value || this._autoAddedHttps) return false
    if (UNSAFE_SCHEMES.includes(schemeOf(value))) return true

    return !KEPT_AS_TYPED.test(value) && LOOKS_LIKE_DOMAIN.test(value)
  }

  onKeydown(event) {
    if (event.isComposing) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      event.stopPropagation()
      this._openDropdown()
      this._whenPanesReady(null, (panes) => {
        this._focusPaneSearch(panes)
      })
      return
    }
    // Stop keystrokes that the input should handle natively (typing,
    // backspace, cursor movement) from bubbling to dropdown#onKeydown /
    // dropdown#onArrowLeft / dropdown#onArrowRight, which would call
    // preventDefault and block them. Enter can still drive dropdown navigation.
    const isPrintable =
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    if (
      isPrintable ||
      event.key === 'Backspace' ||
      event.key === 'ArrowLeft' ||
      event.key === 'ArrowRight'
    ) {
      event.stopPropagation()
    }
    if (event.key === 'Backspace' && this._previewVisible()) {
      event.preventDefault()
      this.clearSelection()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (!this._popPane()) this._closeDropdown()
    }
  }

  // Keep search navigation inside this picker. The shared menu's Left/Right and
  // Tab handlers otherwise swallow caret movement and normal tabbing out.
  onPaneKeydown(event) {
    const back = event.target
    if (back.classList?.contains('t-pane__back') && inActivePane(back)) {
      this.onBackButtonKeydown(event)
      return
    }
    const input = event.target
    const menu = input.closest?.('[data-controller~="menu"]')
    if (!menu || !inActivePane(input) || !input.matches(PANE_SEARCH_SELECTOR))
      return
    if (event.isComposing || event.ctrlKey || event.metaKey || event.altKey)
      return

    if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      if (input.matches('[data-menu-target~="searchBox"]')) {
        this.application
          .getControllerForElementAndIdentifier(menu, 'menu')
          ?.flushLocalSearch()
      } else {
        const picker = this.application.getControllerForElementAndIdentifier(
          input.closest('[data-controller~="object-picker--mini"]'),
          'object-picker--mini'
        )
        // A server search still on its debounce/request must not select a row
        // left over from the previous query.
        if (!picker || picker.searchPending) {
          event.preventDefault()
          event.stopPropagation()
          return
        }
      }
    }

    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (!this._popPane()) this._closeDropdown()
    } else if (event.key === 'Tab') {
      event.stopPropagation()
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.stopPropagation()
      if (event.key === 'ArrowLeft' && !input.value) {
        event.preventDefault()
        input.dispatchEvent(new CustomEvent('panes:pop', { bubbles: true }))
      }
    } else if (event.key === 'Enter') {
      event.preventDefault()
      event.stopPropagation()
      // A held Enter would otherwise repeat into the pane this press just
      // opened and choose its first row before the subitems can be seen.
      if (event.repeat) return
      // menu#onEnterKey restores the old search's focus after clicking. A link
      // selection closes the picker, so that would focus an invisible input.
      this._chooseHighlighted(menu)
    }
  }

  // Parent rows open their pane. The shared menu clicks the first inner
  // control it finds, which is not the row that drills into subitems.
  _chooseHighlighted(menuEl) {
    const menu = this.application.getControllerForElementAndIdentifier(
      menuEl,
      'menu'
    )
    const item = menu?.lastActiveItem
    // Link-picker and object-picker rows handle their own click (with the
    // row's Stimulus params), so click the row rather than an inner control.
    if (
      item?.hasAttribute?.('data-link-picker-terminal-param') ||
      item?.dataset?.action?.includes('object-picker--mini#clickRow')
    ) {
      item.click()
      return
    }

    menu?.selectActiveItem()
  }

  // An empty pane focuses its Back button. The "Insert a link" form submits on
  // any Enter keypress, so going back must cancel the key, not click through.
  onBackButtonKeydown(event) {
    if (!['Enter', ' ', 'ArrowLeft', 'Backspace'].includes(event.key)) return
    event.preventDefault()
    event.stopPropagation()
    if (!event.repeat) this._popPane()
  }

  onDropdownHidden(event) {
    if (event.target !== this._dropdownElement()) return
    // Navigation must not reopen a dismissed menu. A configuration refresh
    // should finish, so reopening still offers the current editor's targets.
    if (this._fetchOpensDropdown) this._fetchController?.abort()
    // Escape closes at document capture before our keydown handler. Wait for
    // native focus movement to finish: Tab also briefly leaves focus on body.
    requestAnimationFrame(() => {
      if (!this.element.isConnected) return
      if (
        document.activeElement !== document.body &&
        !this._findPanesElement()?.contains(document.activeElement)
      )
        return

      this._restoringInputFocus = true
      try {
        this.inputTarget.focus({ preventScroll: true })
      } finally {
        this._restoringInputFocus = false
      }
    })
  }

  /* ===== Item selection ===== */

  onItemClick(event) {
    const { internalUri, virtualPath, label, icon, terminal } = event.params

    if (virtualPath === MEDIA_FILE_VIRTUAL_PATH) {
      this._pickMediaFile(internalUri)
    } else if (terminal) {
      this._selectItem(internalUri || '', label, icon)
      this._closeDropdown()
    } else {
      this.element.setAttribute('aria-busy', 'true')
      this._fetchPane(internalUri, virtualPath).finally(() =>
        this.element.removeAttribute('aria-busy')
      )
    }
  }

  // The row carries the assets prefix (simplero://<account>/assets); the file is linked as its
  // download, which the server resolves to a URL that does not expire.
  async _pickMediaFile(assetsUri) {
    this._closeDropdown()
    // The dialog hands focus back to the URL field as it closes (right after this event), and
    // the field opens the picker on focus — which would cover the link just picked.
    window.addEventListener(
      'assets-dialog:hide',
      () => {
        this._restoringInputFocus = true
        setTimeout(() => (this._restoringInputFocus = false))
      },
      { once: true }
    )
    try {
      const { openMediaDialog } = await this._mediaDialogModule()
      await openMediaDialog({
        mode: 'asset',
        allow_crop: false,
        callback: (asset) => {
          const name = asset.filename || asset.title
          this._selectItem(
            `${assetsUri}/${asset.id}/download`,
            `File: ${name}`,
            'file',
            asset.title || name
          )
        },
      })
    } catch (error) {
      presentAlert(error.message)
    }
  }

  // Loaded on first use: the media dialog is large, and most picks never need it.
  _mediaDialogModule() {
    return import('./media/open_dialog')
  }

  onObjectPickerPick(event) {
    event.stopPropagation()
    const { id, created } = event.detail
    // Each object-picker pane carries the collection URI it lists, so picks keep
    // working when the pane was restored from a saved value or reached via Back.
    const paneDataset = event.target.closest(
      '[data-link-picker-base-uri]'
    )?.dataset
    const baseUri = paneDataset?.linkPickerBaseUri
    if (!baseUri || !id) return
    // A pane for one branch of an inverted member link (e.g. "Remove tag" vs "Add tag")
    // shares its base URI with every other branch of the same resource, so resolving the
    // picked id alone is ambiguous. The pane also carries its own virtual_path, same as any
    // other item (see onItemClick) — forwarding it lets the server disambiguate using the
    // branch instead of guessing from the id alone.
    this.element.setAttribute('aria-busy', 'true')
    // A record picked off the list drills in so its page can be chosen; one just created is
    // answering "link to the thing I made", and the server is closing the picker on the back
    // of the create, so take its own link instead of a pane nobody will see. Either way, the
    // branch (when there is one) still has to go along so the server can disambiguate it.
    return this._fetchPane(
      `${baseUri}/${id}`,
      paneDataset?.linkPickerVirtualPath,
      { selectOwnLink: created }
    )
      .then((status) => {
        if (status === 404) presentAlert(REFUSED_PICK_MESSAGE)
        else if (typeof status === 'number') presentAlert(FAILED_PICK_MESSAGE)
      })
      .finally(() => this.element.removeAttribute('aria-busy'))
  }

  // The surrounding dropdown trigger toggles on mousedown and preventDefaults
  // Enter, either of which would stop the anchor opening its tab — so both
  // arrive stopped, and Enter must not also be prevented.
  onOpenSelected() {
    this._closeDropdown()
  }

  clearSelection({ focus = true } = {}) {
    // Only a created pick's own-link fetch is dropped here. A root refresh still
    // in flight is what the shared dialog needs, even when it clears right after configuring.
    if (this._fetchSelectsOwnLink) this._fetchController?.abort()
    this._restoreController?.abort()
    this.hiddenInputTarget.value = ''
    this.inputTarget.value = ''
    this._autoAddedHttps = false
    this._hidePreview()
    this._popToRoot()
    if (focus) this.inputTarget.focus()
    this._commit()
  }

  setValue(uri, label) {
    if (!uri) {
      this.clearSelection({ focus: false })
      return
    }
    this.hiddenInputTarget.value = uri
    this._autoAddedHttps = false
    if (uri.startsWith('simplero://')) {
      this._applyPreview(label || uri, 'link')
      // The caller only knows the link's uri and text — resolve the real item
      // label/icon and the pane stack it lives in, so editing starts where the
      // item was picked instead of at the root level.
      this._restoreFromValue(uri)
    } else if (uri.startsWith('#')) {
      // A fragment only its caller can name: the dialog knows the link's own text, not the
      // target's, and no endpoint resolves a bare fragment. One this caller doesn't offer
      // (a section since deleted) stays editable text rather than an unnameable chip.
      const item = this._onPageItem(uri)
      if (item) this._applyPreview(item.label || uri, item.icon || 'bookmark')
      else {
        this.inputTarget.value = uri
        this._hidePreview()
      }
    } else {
      this.inputTarget.value = uri
      this._hidePreview()
    }
    this._commit()
  }

  // The caller's on-page targets carry either key dialect — Ruby writes snake_case, the dialog
  // forwards the React picker's camelCase.
  _onPageItem(uri) {
    return this._parsedList(this.onPageItemsValue).find(
      (item) => (item.internalUri || item.internal_uri) === uri
    )
  }

  /* ===== Private helpers ===== */

  // `text` is what the link's own text may default to; it is the label unless the caller says otherwise.
  _selectItem(uri, label, icon, text = label) {
    this.hiddenInputTarget.value = uri
    this._autoAddedHttps = false

    if (uri.startsWith('simplero://') || uri.startsWith('#')) {
      this._applyPreview(label || uri, icon)
    } else {
      this.inputTarget.value = uri
      this._hidePreview()
    }

    this._commit()
    this.element.dispatchEvent(
      new CustomEvent('link-picker:selected', {
        detail: { value: uri, label: text },
        bubbles: true,
      })
    )
  }

  _scheduleTypedCommit() {
    this._cancelTypedCommit()
    this._typedChangeTimer = setTimeout(() => {
      this._typedChangeTimer = null
      this._commit()
    }, TYPED_CHANGE_DELAY)
  }

  _cancelTypedCommit() {
    clearTimeout(this._typedChangeTimer)
    this._typedChangeTimer = null
  }

  // Announces the hidden input's value: `change` for listeners that persist it, `input` for
  // those watching it live. Supersedes any change still waiting on the typing delay.
  _commit() {
    this._cancelTypedCommit()
    this.hiddenInputTarget.dispatchEvent(new Event('change', { bubbles: true }))
    this.hiddenInputTarget.dispatchEvent(new Event('input', { bubbles: true }))
  }

  _applyPreview(label, icon) {
    this._updateOpenLink()
    this.previewLabelTarget.textContent = label
    const iconName = (icon || 'link').replace(/^s-/, '')
    const iconEl = this.previewIconTarget
    const oldFaClass = [...iconEl.classList].find((c) => c.startsWith('fa-'))
    if (oldFaClass) iconEl.classList.replace(oldFaClass, `fa-${iconName}`)
    else iconEl.classList.add(`fa-${iconName}`)
    this._showPreview()
  }

  // An on-page (#anchor) selection has nothing to open.
  _updateOpenLink() {
    if (!this.hasPreviewOpenTarget) return

    const uri = this.hiddenInputTarget.value
    if (!uri.startsWith('simplero://') || !this.openUrlValue) {
      this.previewOpenTarget.classList.add('hidden')
      return
    }

    const url = new URL(this.openUrlValue, window.location.origin)
    url.searchParams.set('internal_uri', uri)
    this.previewOpenTarget.href = url.toString()
    this.previewOpenTarget.classList.remove('hidden')
  }

  async _restoreFromValue(uri) {
    // The restore supersedes any pending pane fetch (its stack includes a fresh
    // root pane), and runs on its own controller so a plain clear/refetch can
    // cancel or outlive it independently.
    this._fetchController?.abort()
    this._restoreController?.abort()
    this._restoreController = new AbortController()

    let response
    try {
      response = await this._pickerFetch(
        { internal_uri: uri, stack: 1 },
        { accept: 'application/json', signal: this._restoreController.signal }
      )
    } catch (error) {
      if (error.name === 'AbortError') return
      console.error('LinkPicker restore failed:', error)
      this._refetchRootAfterFailedRestore()
      return
    }

    // An unresolvable value (deleted record, foreign account) keeps the
    // caller-provided label — still editable, just unresolved. Rebuild the root
    // pane though: this restore superseded any exclusions-driven refetch, and
    // the panes must not stay on stale exclusions.
    if (!response.ok) {
      this._refetchRootAfterFailedRestore()
      return
    }

    const data = await response.json()
    if (data?.select?.label)
      this._applyPreview(data.select.label, data.select.icon)
    if (data?.panes_html) this._setPanes(data.panes_html)
  }

  _refetchRootAfterFailedRestore() {
    this._fetchPane(null, null, { replaceAll: true })
  }

  // Resolves true, or the failing HTTP status.
  async _fetchPane(
    internalUri,
    virtualPath,
    { replaceAll = false, selectOwnLink = false } = {}
  ) {
    // User navigation wins over a pending saved-value restore.
    this._restoreController?.abort()
    this._fetchController?.abort()
    this._fetchController = new AbortController()
    // Creating a record closes the dropdown before its own link finishes resolving.
    this._fetchOpensDropdown = !replaceAll && !selectOwnLink
    this._fetchSelectsOwnLink = selectOwnLink

    let response
    try {
      response = await this._pickerFetch(
        { internal_uri: internalUri, virtual_path: virtualPath },
        {
          accept: 'text/html, application/json',
          signal: this._fetchController.signal,
        }
      )
    } catch (error) {
      if (error.name !== 'AbortError')
        console.error('LinkPicker fetch failed:', error)
      return
    }

    if (!response.ok) {
      console.error(
        'LinkPicker: server returned',
        response.status,
        response.url
      )
      return response.status
    }

    const contentType = response.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const data = await response.json()
      if (data?.select) {
        this._selectItem(
          data.select.uri || '',
          data.select.label,
          data.select.icon
        )
        this._closeDropdown()
      }
      return true
    }

    const html = await response.text()
    const doc = new DOMParser().parseFromString(html, 'text/html')
    const pane = doc.body.firstElementChild
    if (!pane) return true

    if (selectOwnLink) {
      const own = this._ownLinkRow(pane)
      if (own) {
        this._selectItem(own.uri, own.label, own.icon)
        this._closeDropdown()
        return true
      }
    }

    this._whenPanesReady(this._fetchController.signal, (panesEl) => {
      // The shared editor dialog can still be refreshing its root when the
      // user starts searching. Carry that search, from either kind of pane
      // search, into the replacement pane so typed text is never dropped.
      const searchQuery =
        replaceAll &&
        panesEl.contains(document.activeElement) &&
        document.activeElement.matches(PANE_SEARCH_SELECTOR)
          ? document.activeElement.value
          : undefined
      panesEl.dispatchEvent(
        new CustomEvent(replaceAll ? 'panes:set' : 'panes:push', {
          detail: { content: pane },
          bubbles: true,
        })
      )

      // A pick from the full-screen picker modal arrives while the dropdown is
      // closed — reopen it so the pushed pane (the next step) is actually seen.
      if (!replaceAll) {
        this._openDropdown()
        this._focusPaneSearch(panesEl)
      } else if (searchQuery !== undefined) {
        this._focusPaneSearch(panesEl, searchQuery)
      }
    })
  }

  // The link an item stands for, out of the pane listing everything it offers. The first row is
  // the collection's first declared link, which is the one the resolver already treats as
  // representing the item — it is where the item's `long_label` comes from — and it is the
  // navigational one: `simplero://…/products/7/smart` ("Link to product"), a site's home page.
  //
  // Only reached by a collection that is creatable here AND whose members resolve to a pane, which
  // today means products and sites. Anything whose member has a single link (a tag) or only 1-click
  // actions (an automation: start/stop/pause/resume) resolves terminal instead and comes back as a
  // `select` the picker just takes. Nil when a pane offers no link at all, and the pane is pushed
  // as usual.
  _ownLinkRow(pane) {
    const row = pane.querySelector(
      '[data-link-picker-terminal-param="true"][data-link-picker-internal-uri-param]'
    )
    if (!row) return null

    const {
      linkPickerInternalUriParam: uri,
      linkPickerLabelParam: label,
      linkPickerIconParam: icon,
    } = row.dataset
    return uri ? { uri, label, icon } : null
  }

  _pickerFetch(params, { accept, signal }) {
    const search = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value != null) search.set(key, value)
    })

    this._parsedExcludedPaths().forEach((p) =>
      search.append('excluded_paths[]', p)
    )

    if (this.currentSiteIdValue)
      search.set('current_site_id', this.currentSiteIdValue)
    if (this.currentLandingPageIdValue)
      search.set('current_landing_page_id', this.currentLandingPageIdValue)
    if (this.currentFunnelIdValue)
      search.set('current_funnel_id', this.currentFunnelIdValue)
    if (this.onPageSourceValue)
      search.set('on_page_source', this.onPageSourceValue)

    const url = new URL(this.pickerUrlValue, window.location.origin)
    const headers = { Accept: accept, 'X-CSRF-Token': CSRF_TOKEN() }

    // A source-backed picker sends a short signed id, but the "Insert a link" dialog sends the
    // targets themselves, and they travel with every request because the server renders both
    // the rows and the panes behind them from the list. A document's worth of anchors is far
    // too much for a query string, so those requests are POSTed with the parameters in the body.
    if (this._hasCallerItems()) {
      search.set('on_page_items', this.onPageItemsValue || '[]')
      search.set('grouped_resources', this.groupedResourcesValue || '[]')
      return fetch(url.toString(), {
        method: 'POST',
        headers,
        body: search,
        signal,
      })
    }

    url.search = search.toString()
    return fetch(url.toString(), { headers, signal })
  }

  _hasCallerItems() {
    return (
      this._parsedList(this.onPageItemsValue).length > 0 ||
      this._parsedList(this.groupedResourcesValue).length > 0
    )
  }

  // Canonical JSON for a caller-supplied list, so a new one can be compared against what the
  // panes were last built from. Lists arrive as arrays, or — for grouped resources read off a
  // data attribute — as an already-encoded JSON string.
  _listJson(list) {
    return JSON.stringify(
      typeof list === 'string' ? this._parsedList(list) : list || []
    )
  }

  _setPanes(panesHtml) {
    const doc = new DOMParser().parseFromString(panesHtml, 'text/html')
    const panes = [...doc.body.children]
    if (!panes.length) return

    this._whenPanesReady(this._restoreController.signal, (panesEl) => {
      const fragment = document.createDocumentFragment()
      panes.forEach((pane) => fragment.appendChild(pane))
      panesEl.dispatchEvent(
        new CustomEvent('panes:set', {
          detail: { content: fragment },
          bubbles: true,
        })
      )
    })
  }

  // A freshly lazy-instantiated panes element hasn't connected its Stimulus
  // controller yet, so an immediately-dispatched panes event would be lost —
  // wait for the controller before dispatching. The abort signal stops the
  // wait when the originating fetch has been superseded.
  _whenPanesReady(signal, callback) {
    if (signal?.aborted || !this.element.isConnected) return
    const panesEl = this._panesElement()
    if (!panesEl) return
    if (
      this.application.getControllerForElementAndIdentifier(panesEl, 'panes')
    ) {
      callback(panesEl)
    } else {
      requestAnimationFrame(() => this._whenPanesReady(signal, callback))
    }
  }

  // This dropdown's popper lives inside this.element (interactive Tippy appends
  // to the trigger's parent), but also resolve through the dropdown controller's
  // popper, so a dropdown configured to portal its content (append_to_body /
  // append_to_parent_selector) couldn't strand the panes. Passive — never
  // triggers the lazy setup.
  _findPanesElement() {
    const inElement = this.element.querySelector('.t-panes')
    if (inElement) return inElement

    const dropdownEl = this._dropdownElement()
    const dropdown =
      dropdownEl &&
      this.application.getControllerForElementAndIdentifier(
        dropdownEl,
        'dropdown'
      )
    return dropdown?.__content?.querySelector('.t-panes') || null
  }

  _panesElement() {
    let panesEl = this._findPanesElement()
    if (!panesEl) {
      // A lazy dropdown still holds its panes inside a <template> (e.g. when a
      // configure-triggered root refetch lands before the first open) — force
      // the lazy setup so the panes exist to receive the content.
      this._dropdownElement()?.dispatchEvent(
        new CustomEvent('dropdown:setupIfLazy')
      )
      panesEl = this._findPanesElement()
    }
    return panesEl
  }

  _dropdownElement() {
    return this.element.querySelector('[data-controller~="dropdown"]')
  }

  _focusPaneSearch(panes, query) {
    const menu = panes?.querySelector(
      ':scope > .t-pane--active [data-controller~="menu"]'
    )
    if (!menu) {
      // An empty pane ("No items found") has nothing to search. Keep focus in
      // the picker, not on the page, so Enter/Escape can still go back.
      panes
        ?.querySelector(':scope > .t-pane--active .t-pane__back')
        ?.focus({ preventScroll: true })
      return
    }
    // Mini pickers have their own server-backed search alongside Menu's hidden
    // local search. Focus the one that can search the entire collection.
    const input =
      menu.querySelector('[data-object-picker--mini-target~="searchInput"]') ||
      menu.querySelector('[data-menu-target~="searchBox"]')
    input
      ?.closest('[data-menu-target~="searchBoxContainer"]')
      ?.classList.remove('hidden')
    if (input && query) {
      input.value = query
      // panes#set inserts synchronously, so Stimulus connects the replacement
      // menu (clearing its search) in the MutationObserver microtask already
      // queued. Reapply right after that, before any keystroke can land.
      queueMicrotask(() => {
        if (input.isConnected) {
          input.value = query
          input.dispatchEvent(new Event('input', { bubbles: true }))
        }
      })
    }
    input?.focus({ preventScroll: true })
    return menu
  }

  _openDropdown() {
    this._dropdownElement()?.dispatchEvent(new CustomEvent('dropdown:open'))
  }

  _closeDropdown() {
    if (this._findPanesElement()?.contains(document.activeElement)) {
      this.inputTarget.focus({ preventScroll: true })
    }
    this._dropdownElement()?.dispatchEvent(new CustomEvent('dropdown:close'))
  }

  _popToRoot() {
    this._findPanesElement()?.dispatchEvent(
      new CustomEvent('panes:popToRoot', { bubbles: true })
    )
  }

  _popPane() {
    const panes = this._findPanesElement()
    const stack = panes?.querySelectorAll(':scope > .t-pane')
    if (!stack || stack.length < 2) return false

    // A popped pane remains in the DOM until its slide ends, with the previous
    // pane already active. Don't pop it twice, but allow Escape during a push.
    if (
      panes.classList.contains('t-panes--transitioning') &&
      !stack[stack.length - 1].classList.contains('t-pane--active')
    )
      return true

    if (this._fetchOpensDropdown) this._fetchController?.abort()
    this._restoreController?.abort()
    panes.dispatchEvent(new CustomEvent('panes:pop', { bubbles: true }))
    return true
  }

  _showPreview() {
    this.previewTarget.classList.remove('hidden')
    this.inputTarget.classList.add(...PREVIEW_INPUT_CLASSES)
    this.inputTarget.value = ''
  }

  _hidePreview() {
    this.previewTarget.classList.add('hidden')
    this.inputTarget.classList.remove(...PREVIEW_INPUT_CLASSES)
  }

  _previewVisible() {
    return !this.previewTarget.classList.contains('hidden')
  }

  _parsedExcludedPaths() {
    return this._parsedList(this.excludedPathsValue)
  }

  _parsedList(json) {
    try {
      const parsed = JSON.parse(json || '[]')
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }
}
